import { createSelector } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { MemberStates } from 'src/models/enums/memberStates.enum';
import { SpaceTypes } from 'src/models/enums/spaceTypes.enum';
import {
  User,
  UserOrganisation,
  UserWorkspace
} from 'src/models/user.interface';

import useAuth from './useAuth';

type Space =
  | (UserWorkspace & {
      spaceType: SpaceTypes.WORKSPACE;
    })
  | (UserWorkspace &
      UserOrganisation & {
        spaceType: SpaceTypes.ORGANISATION;
      });

export const selectSpaceFromUser = createSelector(
  (user: User) => user,
  ({ selectedSpace, workspaces, organisations }): Space => {
    const selectedWorkspace = workspaces?.find(
      (w) => w._id === selectedSpace && w.member.state === MemberStates.APPROVED
    );

    const selectedOrganisation = organisations?.find(
      (org) => org._id === selectedSpace
    );

    if (selectedWorkspace) {
      return { ...selectedWorkspace, spaceType: SpaceTypes.WORKSPACE };
    } else if (selectedOrganisation) {
      const firstWorkspaceInOrg = workspaces.find((w) =>
        selectedOrganisation.workspaces.includes(w._id)
      );
      return {
        ...firstWorkspaceInOrg,
        ...selectedOrganisation,
        spaceType: SpaceTypes.ORGANISATION
      };
    }
  }
);

export const useSpace = () => {
  const { user } = useAuth();

  const space = useMemo(() => selectSpaceFromUser(user), [user]);

  return { ...space, spaceId: space._id };
};
